// Fitting combined-orders' action menu into the app header's center slot.
//
// The header row is a fixed 36px (DevExtreme's toolbar), so a menu that wraps
// there spills its second line over the page instead of growing the header
// (GLP-127). The menu therefore takes the first stage that fits on one line,
// each shorter than the last, and falls back to the top of the page when
// none does:
//
//   0  header, everything spelled out
//   1  header, action buttons icon-only (their hints still name them)
//   2  header, the remark / drop-off notices icon-only too (their hover
//      tooltips remain)
//   3  top of the page, spelled out, wrapping as it needs
//
// Unlike revenue-fees (GLP-133), whose controls are fixed, this menu changes
// with the data — notices and counts come and go, the buttons follow the
// tab — so it is re-measured whenever its content changes, wherever it is
// rendered.

/** The classes each header stage puts on `.header-page-actions`. */
export const HEADER_STAGE_CLASSES: readonly (readonly string[])[] = [
    [],
    ['icon-buttons'],
    ['icon-buttons', 'icon-notices'],
];

/** The stage that renders the menu in the page rather than the header. */
export const IN_PAGE_STAGE = HEADER_STAGE_CLASSES.length;

// Moving the menu to a roomier stage needs this much slack. The menu is
// measured where it is rendered, header or page; should the two ever lay it
// out a few pixels apart, the margin stops it flipping between them.
const UPGRADE_MARGIN_PX = 8;

const STAGE_CLASSES = [...new Set(HEADER_STAGE_CLASSES.flat())];

/**
 * The menu's one-line width at each header stage, as the header row would
 * lay it out. The classes are set for these synchronous reads only and
 * restored — to exactly what Angular's bindings left — before the browser
 * paints.
 */
export const oneLineWidths = (menu: HTMLElement): number[] => {
    const had = STAGE_CLASSES.filter((name) => menu.classList.contains(name));
    menu.classList.add('measuring');
    const widths = HEADER_STAGE_CLASSES.map((classes) => {
        for (const name of STAGE_CLASSES) {
            menu.classList.toggle(name, classes.includes(name));
        }
        return Math.ceil(menu.getBoundingClientRect().width);
    });
    for (const name of STAGE_CLASSES) {
        menu.classList.toggle(name, had.includes(name));
    }
    menu.classList.remove('measuring');
    return widths;
};

/**
 * The first header stage whose one-line width fits the slot, else
 * IN_PAGE_STAGE. Keeps `current` while there is nothing to decide on: no
 * slot width yet (or no app header at all) or no measurement.
 */
export const fitMenuStage = (
    widths: readonly number[],
    slotWidth: number,
    current: number,
): number => {
    if (slotWidth <= 0 || !(widths[0] > 0)) {
        return current;
    }
    for (let stage = 0; stage < widths.length; stage++) {
        const margin = stage < current ? UPGRADE_MARGIN_PX : 0;
        if (widths[stage] <= slotWidth - margin) {
            return stage;
        }
    }
    return IN_PAGE_STAGE;
};
